import { createSlice } from '@reduxjs/toolkit';

const initialState = {
  firmwareVersions: [],
  selectedFirmwareVersion: null,
  total: 0,
  page: 1,
  pageSize: 10,
  filterVersion: '',
  filterDeviceType: null,
  sortColumn: 'id',
  sortDirection: 'desc',
  loading: false,
  error: null,
  successMessage: null,
};

const firmwareVersionsSlice = createSlice({
  name: 'firmwareVersions',
  initialState,
  reducers: {
    // Fetch firmware versions
    fetchFirmwareVersions: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchFirmwareVersionsSuccess: (state, action) => {
      state.firmwareVersions = action.payload.data;
      state.total = action.payload.meta.total;
      state.loading = false;
      state.error = null;
    },
    fetchFirmwareVersionsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Fetch firmware version by ID
    fetchFirmwareVersionById: (state) => {
      state.loading = true;
      state.error = null;
    },
    fetchFirmwareVersionByIdSuccess: (state, action) => {
      state.selectedFirmwareVersion = action.payload;
      state.loading = false;
      state.error = null;
    },
    fetchFirmwareVersionByIdFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Add firmware version
    addFirmwareVersion: (state) => {
      state.loading = true;
      state.error = null;
    },
    addFirmwareVersionSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    addFirmwareVersionFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Update firmware version
    updateFirmwareVersion: (state) => {
      state.loading = true;
      state.error = null;
    },
    updateFirmwareVersionSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.selectedFirmwareVersion = null;
      state.error = null;
    },
    updateFirmwareVersionFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Delete firmware version
    deleteFirmwareVersion: (state) => {
      state.loading = true;
      state.error = null;
    },
    deleteFirmwareVersionSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    deleteFirmwareVersionFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // Bulk delete firmware versions
    bulkDeleteFirmwareVersions: (state) => {
      state.loading = true;
      state.error = null;
    },
    bulkDeleteFirmwareVersionsSuccess: (state, action) => {
      state.loading = false;
      state.successMessage = action.payload.message;
      state.error = null;
    },
    bulkDeleteFirmwareVersionsFailure: (state, action) => {
      state.loading = false;
      state.error = action.payload;
    },

    // UI state management
    setPage: (state, action) => {
      state.page = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
      state.page = 1;
    },
    setFilterVersion: (state, action) => {
      state.filterVersion = action.payload;
      state.page = 1;
    },
    setFilterDeviceType: (state, action) => {
      state.filterDeviceType = action.payload;
      state.page = 1;
    },
    setSortColumn: (state, action) => {
      state.sortColumn = action.payload;
    },
    setSortDirection: (state, action) => {
      state.sortDirection = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    clearSuccessMessage: (state) => {
      state.successMessage = null;
    },
    clearSelectedFirmwareVersion: (state) => {
      state.selectedFirmwareVersion = null;
    },
  },
});

export const {
  fetchFirmwareVersions,
  fetchFirmwareVersionsSuccess,
  fetchFirmwareVersionsFailure,
  fetchFirmwareVersionById,
  fetchFirmwareVersionByIdSuccess,
  fetchFirmwareVersionByIdFailure,
  addFirmwareVersion,
  addFirmwareVersionSuccess,
  addFirmwareVersionFailure,
  updateFirmwareVersion,
  updateFirmwareVersionSuccess,
  updateFirmwareVersionFailure,
  deleteFirmwareVersion,
  deleteFirmwareVersionSuccess,
  deleteFirmwareVersionFailure,
  bulkDeleteFirmwareVersions,
  bulkDeleteFirmwareVersionsSuccess,
  bulkDeleteFirmwareVersionsFailure,
  setPage,
  setPageSize,
  setFilterVersion,
  setFilterDeviceType,
  setSortColumn,
  setSortDirection,
  clearError,
  clearSuccessMessage,
  clearSelectedFirmwareVersion,
} = firmwareVersionsSlice.actions;

// Selectors
export const selectFirmwareVersions = (state) => state.firmwareVersions?.firmwareVersions || [];
export const selectSelectedFirmwareVersion = (state) => state.firmwareVersions?.selectedFirmwareVersion;
export const selectFirmwareVersionsLoading = (state) => state.firmwareVersions?.loading || false;
export const selectFirmwareVersionsError = (state) => state.firmwareVersions?.error;
export const selectFirmwareVersionsSuccessMessage = (state) => state.firmwareVersions?.successMessage;
export const selectFirmwareVersionsTotal = (state) => state.firmwareVersions?.total || 0;
export const selectFirmwareVersionsPage = (state) => state.firmwareVersions?.page || 1;
export const selectFirmwareVersionsPageSize = (state) => state.firmwareVersions?.pageSize || 10;
export const selectFirmwareVersionsSortColumn = (state) => state.firmwareVersions?.sortColumn || 'id';
export const selectFirmwareVersionsSortDirection = (state) => state.firmwareVersions?.sortDirection || 'desc';
export const selectFirmwareVersionsFilterVersion = (state) => state.firmwareVersions?.filterVersion || '';
export const selectFirmwareVersionsFilterDeviceType = (state) => state.firmwareVersions?.filterDeviceType;

export default firmwareVersionsSlice.reducer;
